import React, { useState, useContext, useEffect, useRef } from 'react';
import { DocumentLinesRowsFieldType, FieldData } from 'types/FieldTypes';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { baseUrl } from 'App';
import { useSettings } from 'services/settings/SettingsContext';
import { useAllowedRight, useActiveFeature } from 'services/permissions/permissionChecks';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { fetchData } from 'services/api/fetchData';
import { resolveMatrixPrice, parseDimensionInput } from 'services/utils/resolveMatrixPrice';
import FormFieldContext from '../FormFieldContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripLines, faPen, faSearch, faTrash, faStickyNote, faImage, faTimes } from '@fortawesome/free-solid-svg-icons';
import { DocumentLineType, DocumentLineGroupType, createNewDocumentLine, createNewDocumentLineGroup } from '../../../views/invoices/InvoiceTypes';
import Dropdown from '../basefields/Dropdown';
import { DragDropContext, Draggable, DropResult } from 'react-beautiful-dnd';
import { StrictModeDroppable } from 'services/utils/dragDropUtils';
import { parseQuantityAndUnit } from 'services/utils/parseQuantityAndUnit';
import { formatHoursQuantity, isHourUnit } from 'services/utils/hourQuantity';
import { v4 as uuidv4 } from 'uuid';
import { LedgerAccountType, VatRateType } from '../../../views/settings/SettingsTypes';
import { CurrencyType, PriceMatrixType, ProductType } from '../../../views/products/ProductTypes';
import '../../../style/scss/live-edit.scss';
import '../../../style/scss/forms.scss';
import { formatPriceCurrencyString, formatPriceString } from 'services/utils/amountFormatting';
import { renderRowsFieldErrorMessages } from './functions/renderRowsFieldErrorMessages';
import TextareaInput from '../basefields/TextareaInput';
import TabsButton from 'components/buttons/TabsButton';
import SearchField from '../basefields/SearchField';
import SecondaryButton from 'components/buttons/SecondaryButton';

const DocumentLinesRowsField: React.FC<DocumentLinesRowsFieldType & { data: FieldData }> = ({
    name, featureName, documentId, data, dropdownData, helperText, isEditable
}) => {
    const { t } = useTranslation();
    const { editing, setUpdatedData, showErrorAlert, setFooterLeftContent } = useContext(FormFieldContext);
    const { errorMessages } = useGlobalContext();
    const { userLocale, defaultCurrency, defaultCurrencySymbol } = useSettings();
    const hasRightCheck = useAllowedRight;
    const [rows, setRows] = useState<DocumentLineType[]>([]);
    const [sections, setSections] = useState<DocumentLineGroupType[]>([]);
    const [selectedCurrency, setSelectedCurrency] = useState<string>('eur');
    const [vatRates, setVatRates] = useState<VatRateType[]>([]);
    const [ledgerAccounts, setLedgerAccounts] = useState<LedgerAccountType[]>([]);
    const [currencies, setCurrencies] = useState<CurrencyType[]>([]);
    const [userHasOnlyViewRights] = useState<boolean>(hasRightCheck('only_view'));
    const [vatCalculationMethod, setVatCalculationMethod] = useState<'vat_included' | 'vat_excluded'>('vat_excluded');
    const priceDisplayModes = [
        { name: 'document_lines.price_display_mode.line_prices', value: 'line_prices'},
        { name: 'document_lines.price_display_mode.total_price', value: 'total_price'}
    ];
    const [priceDisplayMode, setPriceDisplayMode] = useState<{ name: string, value: string }>({ name: 'document_lines.price_display_mode.line_prices', value: 'line_prices'});
    const [showVatReverseCharge, setShowVatReverseCharge] = useState(false);
    const [vatReverseChargeNumber, setVatReverseChargeNumber] = useState<string | null>(null);
    const [visibleSearchField, setVisibleSearchField] = useState<string | null>(null);
    const [visibleDescriptionField, setVisibleDescriptionField] = useState<string | null>(null);
    const { handleLogout } = useAuthContext();
    const dimensionPricingActive = useActiveFeature('dimension_pricing');
    const lineImagesAllowed = dimensionPricingActive && (featureName === 'proposal' || featureName === 'order');
    const [productMatrices, setProductMatrices] = useState<Record<number, PriceMatrixType | null>>({});
    const [missingMatrixPrice, setMissingMatrixPrice] = useState<Record<string, boolean>>({});
    const rowsRef = useRef<DocumentLineType[]>([]);

    // Keep a ref of the rows for handlers which continue after a fetch
    useEffect(() => {
        rowsRef.current = rows;
    }, [rows]);

    // If there are document lines in the fetched data, show them. Otherwise show an empty row
    useEffect(() => {
        if (data[name]) {
            // Get the currency from the data or fallback to the default currency
            const currencyKey = data['currency'] ?? defaultCurrency ?? 'EUR';

            // Check if the recipient has vat reverse charge
            const recipientHasVatReverseCharge = data['recipient']?.['vat_reverse_charge'] === true;

            // Find the first vat rate with the vat reverse charge property
            const vatReverseChargeRate = dropdownData?.vatrate?.results?.find(
                (vatRate: VatRateType) => vatRate.vat_reverse_charge === true
            );

            // Find the default vat rate
            const defaultVatRate = dropdownData?.vatrate?.results?.find(
                (vatRate: VatRateType) => vatRate.is_default === true
            );

            // Set the display mode from the data
            const currentDisplayMode = priceDisplayModes.find(displayMode => data['price_display_mode'] === displayMode.value);
            if (currentDisplayMode) {
                setPriceDisplayMode(currentDisplayMode);
            }

            // Set the vat calculation method from the data
            setVatCalculationMethod(data['vat_method']);

            // Set the fetched line groups, sorted by position
            const fetchedGroups: DocumentLineGroupType[] = data['line_groups'] || [];
            const sortedGroups = [...fetchedGroups].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
            setSections(sortedGroups);

            // Build a map from group identifier to its position, to sort lines by their section first
            const groupPositions = new Map<string, number>();
            sortedGroups.forEach(group => {
                const key = group.id !== null ? group.id.toString() : (group.uuid as string);
                groupPositions.set(key, group.position ?? 0);
            });

            // Set the fetched rows from the data
            if (data[name].length > 0) {
                const sortedRows = (data[name] || []).sort((a: any, b: any) => {
                    // Lines without a group belong to the not yet materialized first section, sort them first
                    const positionOfGroup = (row: any) => row.group !== null && row.group !== undefined
                        ? (groupPositions.get(row.group.toString()) ?? 0)
                        : -1;
                    const groupPositionDifference = positionOfGroup(a) - positionOfGroup(b);
                    return groupPositionDifference !== 0 ? groupPositionDifference : a.position - b.position;
                }).map((row: any) => ({
                    ...row,
                    quantity_string: formatQuantityUnit(row.quantity, userLocale, row.unit),
                    sale_price_string: formatPriceForDisplay(row.sale_price, userLocale),
                    sale_price_currency: currencyKey,
                }));

                // Check if any row has a vat rate with vat reverse charge
                const hasVatReverseCharge = sortedRows.some((row: DocumentLineType) => {
                    const vatRate = dropdownData?.vatrate?.results?.find(
                        (rate: VatRateType) => rate.id === row.vat_rate
                    );
                    return vatRate?.vat_reverse_charge === true;
                });

                // Set the rows
                setRows(sortedRows)

                // Show the vat reverse charge and vat number
                setShowVatReverseCharge(recipientHasVatReverseCharge && hasVatReverseCharge);
                setVatReverseChargeNumber(data['recipient']?.['vat_number']);

            // If no rows are fetched, show an empty row
            } else {
                // Attach the empty row to the first existing section, if any sections already exist
                const firstSection = sortedGroups[0];
                const initialGroup = firstSection ? (firstSection.id !== null ? firstSection.id : (firstSection.uuid ?? null)) : null;

                // Create a new empty row
                const newRow = createNewDocumentLine(documentId!, featureName as 'proposal' | 'order' | 'invoice', uuidv4(), currencyKey, 1, initialGroup);

                // If recipient has vat reverse charge, update the vat rate in the empty row
                if (recipientHasVatReverseCharge && vatReverseChargeRate) {
                    newRow.vat_rate = vatReverseChargeRate.id;
                }

                // Otherwise if a default vat rate is set, set this as the vat rate
                else if (defaultVatRate) {
                    newRow.vat_rate = defaultVatRate.id;
                }

                // Show the vat reverse charge and vat number
                setShowVatReverseCharge(recipientHasVatReverseCharge);
                setVatReverseChargeNumber(data['recipient']?.['vat_number']);

                // Set the new row
                setRows([newRow]);
            }
        }
    }, [data, documentId, dropdownData]);

    // Set the vat rates from the dropdown data
    useEffect(() => {
        setVatRates(dropdownData?.vatrate?.results || []);
        setLedgerAccounts(dropdownData?.ledgeraccount?.results || []);
        setCurrencies(dropdownData?.currency?.results || []);
    }, [dropdownData])

    // Set the selected currency
    useEffect(() => {
        if (data && dropdownData) {
            // Base the currency on the data, or fallback
            const currencyKey = data['currency'] ?? defaultCurrency ?? 'EUR'

            const selectedCurrencyOption = currencies.find(currency => currency.key == currencyKey);

            // When an option is found, set the selected currency
            if (selectedCurrencyOption) {
                setSelectedCurrency(selectedCurrencyOption.key)
            }
        }
    }, [data, dropdownData])

    // Register the add section footer button, only while editing
    useEffect(() => {
        if (!setFooterLeftContent) return;

        if (editing) {
            setFooterLeftContent(
                <SecondaryButton
                    onClick={() => handleAddSection()}
                    label="document_lines.sections.add_section_button"
                    size="extra-small"
                />
            );
        } else {
            setFooterLeftContent(null);
        }

        return () => setFooterLeftContent(null);
    }, [editing, sections, rows, data, documentId, dropdownData, showVatReverseCharge]);

    // Format the backend quantity and unit to show in the quantity_string field
    const formatQuantityUnit = (quantity: number | null, userLocale: string, unit?: string): string => {
        // If quantity is null, don't show anything
        if (quantity === null || quantity === undefined) {
            return ''
        }

        // Show the quantity of an hour unit as hours and minutes
        if (isHourUnit(unit)) {
            return `${formatHoursQuantity(quantity)} ${unit}`;
        }

        // If the unit is a percentage sign, multiply the quantity by 100 to show it as a percentage
        const adjustedQuantity = unit === '%' ? quantity * 100 : quantity;

        // Format the quantity based on the user locale
        const formattedQuantity = new Intl.NumberFormat(userLocale, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 4,
        }).format(adjustedQuantity);

        // Combine the formatted quantity with the unit, if it exists
        return unit ? `${formattedQuantity} ${unit}` : formattedQuantity;
    };

    // Format the backend sale_price to show in the sale_price_string field
    const formatPriceForDisplay = (price: number | null, userLocale: string): string | undefined => {
        if (price === null || isNaN(price)) {
            return undefined;
        };

        // Format the price
        return new Intl.NumberFormat(userLocale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(price);
    };

    // Get the identifier of a section, either its saved id or its uuid while unsaved
    const sectionIdentifierOf = (section: DocumentLineGroupType): string => {
        return section.id !== null ? section.id.toString() : (section.uuid as string);
    };

    // Get the section identifier a row belongs to
    const rowSectionIdentifier = (row: DocumentLineType): string | undefined => {
        return row.group !== null && row.group !== undefined ? row.group.toString() : undefined;
    };

    // Get the rows which belong to a section, the first section also collects rows without a group
    const rowsOfSection = (sectionId: string, isFirstSection: boolean): DocumentLineType[] => {
        return rows.filter(row => {
            if (row.deleted) return false;
            const rowGroup = rowSectionIdentifier(row);
            if (rowGroup === sectionId) return true;
            return isFirstSection && rowGroup === undefined;
        });
    };

    // Renumber the position of every row, scoped to its own section when sections are used
    const renumberRows = (allRows: DocumentLineType[], allSections: DocumentLineGroupType[]): DocumentLineType[] => {
        const nonDeletedSections = allSections.filter(section => !section.deleted);
        const deletedRows = allRows.filter(row => row.deleted);

        // Without sections, positions are numbered document wide, same as before sections existed
        if (nonDeletedSections.length === 0) {
            const nonDeletedRows = allRows.filter(row => !row.deleted);
            const renumberedRows = nonDeletedRows.map((row, index) => ({ ...row, position: index + 1 }));
            return [...renumberedRows, ...deletedRows];
        }

        // With sections, renumber the rows of every section on their own
        let renumberedNonDeletedRows: DocumentLineType[] = [];
        nonDeletedSections.forEach((section, sectionIndex) => {
            const sectionId = sectionIdentifierOf(section);
            const sectionRows = allRows.filter(row => {
                if (row.deleted) return false;
                const rowGroup = rowSectionIdentifier(row);
                return rowGroup === sectionId || (sectionIndex === 0 && rowGroup === undefined);
            });
            const renumberedSectionRows = sectionRows.map((row, index) => ({ ...row, position: index + 1 }));
            renumberedNonDeletedRows = [...renumberedNonDeletedRows, ...renumberedSectionRows];
        });

        return [...renumberedNonDeletedRows, ...deletedRows];
    };

    // Generic function to update the rows with a changed value
    const updateRows = (identifier: string, updateFunction: (item: DocumentLineType, ...args: any[]) => DocumentLineType, ...args: any[]) => {
        // Copy the current rows and find its index
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);

        if (rowIndex !== -1) {
            // Set the new value in the specific row
            updatedRows[rowIndex] = updateFunction(updatedRows[rowIndex], ...args);

            // Update the rows and updated data
            setRows(updatedRows);
            setUpdatedData((prev: any) => ({ ...prev, [name]: updatedRows }))
        }
    };

    // Handle the quantity change
    const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>, identifier: string) => {
        // Copy the rows and find the item index
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);

        if (rowIndex !== -1) {
            // Parse the quantity and unit from the quantity field
            const { quantity, unit } = parseQuantityAndUnit(event.target.value)

            // Set the quantity and unit in the updated rows
            updatedRows[rowIndex].quantity_string = event.target.value;
            updatedRows[rowIndex].quantity = quantity;
            updatedRows[rowIndex].unit = unit;

            // Calculate the total price change, only if a sale price and quantity is set
            if (updatedRows[rowIndex].sale_price !== null && quantity !== null) {
                const salePrice = updatedRows[rowIndex].sale_price || 0;
                updatedRows[rowIndex].total_price = quantity * salePrice;
            }

            // Update the rows and updated data
            setRows(updatedRows);
            setUpdatedData((prev: any) => ({ ...prev, [name]: updatedRows }))
        }
    };

    // Handle the price change
    const handlePriceChange = (event: React.ChangeEvent<HTMLInputElement>, identifier: string) => {
        // Copy the rows and find the item index
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);

        if (rowIndex !== -1) {
            // Convert the input into a number
            const salePriceNumber = parseFloat(event.target.value.replace(',', '.'));

            // Set the sale price in the updated rows
            updatedRows[rowIndex].sale_price_string = event.target.value;
            updatedRows[rowIndex].sale_price = salePriceNumber;

            // If sale price is set, calculate the total price
            if (salePriceNumber !== null) {
                const quantity = updatedRows[rowIndex].quantity || 0;
                updatedRows[rowIndex].total_price = quantity * salePriceNumber;
            }

            // Update the rows
            setRows(updatedRows);

            // Convert the sale price to a number for the backend
            setUpdatedData((currentUpdatedData: any) => {
                const copyOfUpdatedData = { ...currentUpdatedData };

                // If no updated data exist, create a new array
                if (!copyOfUpdatedData[name]) {
                    copyOfUpdatedData[name] = [];
                }

                // Find the row from the updated data to update
                const updatedIndex = copyOfUpdatedData[name].findIndex((item: any) => item.id?.toString() === identifier || item.uuid === identifier);

                // Set the sale price in the updated data for the found row or add the value to the new row
                if (updatedIndex !== -1) {
                    copyOfUpdatedData[name][updatedIndex] = {
                        ...updatedRows[rowIndex],
                        sale_price: salePriceNumber ? salePriceNumber : null
                    };
                } else {
                    copyOfUpdatedData[name].push({
                        ...updatedRows[rowIndex],
                        sale_price: salePriceNumber ? salePriceNumber : null
                    });
                }

                return copyOfUpdatedData;
            });
        }
    }

    // Handle text area change
    const handleTextAreaChange = (value: string | React.ChangeEvent<HTMLTextAreaElement>, identifier: string) => {
        // Copy the rows and find the row index
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(item => item.id?.toString() === identifier || item.uuid === identifier);

        if (rowIndex !== -1) {
            // Set the description in the updated rows
            updatedRows[rowIndex].description = String(value);

            // Update rows and updated data
            setRows(updatedRows);
            setUpdatedData((prev: any) => ({ ...prev, [name]: updatedRows }))
        }
    }

    // Handle text area tab to focus to the sale price field
    const handleTextareaTab = (identifier: string) => {
        const element = document.getElementById(`sale_price_string_${identifier}`)

        if (element) {
            element.focus();
        }
    };

    // Handle vat calculation method change
    const handleVatCalculationMethodChange = (vatCalculationMethod: 'vat_excluded' | 'vat_included') => {

        // Update the vat calculation method state
        setVatCalculationMethod(vatCalculationMethod);

        // Update the method in the updated data
        setUpdatedData((currentUpdatedData: any) => ({ ...currentUpdatedData, ['vat_method']: vatCalculationMethod }));
    };

    // Handle price display mode change
    const handlePriceDisplayModeChange = (selectedValue: string) => {
        const selectedOption = priceDisplayModes.find(displayMode => displayMode.value === selectedValue);

        if (selectedOption) {
            // Update the price display mode state
            setPriceDisplayMode(selectedOption)

            // Update the updated data
            setUpdatedData((currentUpdatedData: any) => ({ ...currentUpdatedData, ['price_display_mode']: selectedOption.value }));
        }
    }

    // Handle vat reverse charge number change
    const handleVatReverseChargeNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {

        // Update the vat number
        setVatReverseChargeNumber(event.target.value);

        // Update the vat number in the updated data
        setUpdatedData((currentUpdatedData: any) => ({ ...currentUpdatedData, ['recipient_vat_number']: event.target.value }));
    };

    // Handle the vat rate change
    const handleVatRateChange = (selectedValue: string, identifier: string) => {
        updateRows(identifier, (item, newVatRate) => {
            // Set the new vat rate
            const updatedItem = {
                ...item,
                vat_rate: parseFloat(newVatRate)
            };

            // Check if the new vat rate has vat reverse charge
            const vatRate = dropdownData?.vatrate?.results?.find(
                (rate: VatRateType) => rate.id === parseFloat(newVatRate)
            );

            if (vatRate) {
                setShowVatReverseCharge(vatRate.vat_reverse_charge);
            }

            return updatedItem;
        }, selectedValue);
    };

    // Handle the ledger account change
    const handleLedgerAccountChange = (selectedValue: string, identifier: string) => {
        // Copy the rows and find the row index
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);

        if (rowIndex !== -1) {
            // Set the selected value as
            updatedRows[rowIndex].ledger_account = parseFloat(selectedValue)

            // Update the rows and updated data
            setRows(updatedRows);
            setUpdatedData((currentUpdatedData: any) => ({ ...currentUpdatedData, [name]: updatedRows }))
        }
    };

    // Handle the product search selection
    const handleSelectProduct = (selectedProduct: ProductType, identifier: string) => {
        // Copy the rows and find the row index
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(item => item.id?.toString() === identifier || item.uuid === identifier);

        if (rowIndex !== -1) {
            let salePriceNumber;
            let productNameDescription;
            let quantity = updatedRows[rowIndex].quantity || 0;

            // Combine the name and description of the product
            if (selectedProduct.name !== null && selectedProduct.description !== null) {
                productNameDescription = `${selectedProduct.name}<br>${selectedProduct.description}`;
            } else {
                productNameDescription = selectedProduct.name;
            }

            // Convert the sale price into a number
            if (selectedProduct.sale_price !== null) {
                salePriceNumber = parseFloat(String(selectedProduct.sale_price));
                updatedRows[rowIndex].sale_price = parseFloat(String(selectedProduct.sale_price));
                updatedRows[rowIndex].sale_price_string = String(selectedProduct.sale_price);
                updatedRows[rowIndex].sale_price_currency = selectedProduct.sale_price_currency;
            }

            // Copy the values of the product to the document line
            updatedRows[rowIndex].product = selectedProduct.id;
            updatedRows[rowIndex].description = productNameDescription;
            updatedRows[rowIndex].vat_rate = selectedProduct.vat_rate;
            updatedRows[rowIndex].ledger_account = selectedProduct.ledger_account;

            // Calculate the total price change if a quantity exists and sale price is set
            if (quantity > 0 && salePriceNumber !== undefined) {
                updatedRows[rowIndex].total_price = (quantity * salePriceNumber);
            }

            // Width x depth priced products wait for the size before the price is known
            const pricingMethod = dimensionPricingActive ? (selectedProduct.pricing_method_effective ?? 'per_unit') : null;
            updatedRows[rowIndex].product_pricing_method = pricingMethod;
            if (pricingMethod === 'dimensions') {
                setProductMatrices(prev => ({ ...prev, [selectedProduct.id]: selectedProduct.price_matrix ?? null }));
                updatedRows[rowIndex].width = null;
                updatedRows[rowIndex].depth = null;
                updatedRows[rowIndex].sale_price = 0;
                updatedRows[rowIndex].sale_price_string = formatPriceForDisplay(0, userLocale) ?? null;
                updatedRows[rowIndex].total_price = 0;

            // Other products have no size, so the size fields disappear from the line
            } else {
                updatedRows[rowIndex].width = null;
                updatedRows[rowIndex].depth = null;
            }

            // Update rows and updated data
            setRows(updatedRows);
            setUpdatedData((prev: any) => ({ ...prev, [name]: updatedRows }));

            // Close the search field
            setVisibleSearchField(null);
        }
    };

    // Get the price matrix of a product, fetched once and cached per product
    const resolveProductMatrix = async (productId: number): Promise<PriceMatrixType | null> => {
        if (productId in productMatrices) return productMatrices[productId];
        const product = await fetchData({ apiUrl: `get_product/${productId}`, handleLogout }).catch(() => null);
        const matrix: PriceMatrixType | null = product?.price_matrix ?? null;
        setProductMatrices(prev => ({ ...prev, [productId]: matrix }));
        return matrix;
    };

    // Set the price of a row from the price matrix of its product, no matching size gives a zero price
    const applyMatrixPrice = (identifier: string, matrix: PriceMatrixType | null) => {
        const updatedRows = [...rowsRef.current];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);
        if (rowIndex === -1) return;

        // Resolve the price of the size, rounded up to the next matrix size
        const row = updatedRows[rowIndex];
        const price = resolveMatrixPrice(matrix, row.width ?? null, row.depth ?? null);
        const hasSize = row.width !== null && row.width !== undefined && row.depth !== null && row.depth !== undefined;
        setMissingMatrixPrice(prev => ({ ...prev, [identifier]: hasSize && price === null }));

        // Set the resolved or zero price and the total
        const salePrice = price ?? 0;
        updatedRows[rowIndex] = {
            ...row,
            sale_price: salePrice,
            sale_price_string: formatPriceForDisplay(salePrice, userLocale) ?? null,
            total_price: (row.quantity || 0) * salePrice,
        };

        // Update the rows and updated data
        setRows(updatedRows);
        setUpdatedData((prev: any) => ({ ...prev, [name]: updatedRows }));
    };

    // Handle a width or depth change, the price follows the size of width x depth priced products
    const handleDimensionChange = async (field: 'width' | 'depth', event: React.ChangeEvent<HTMLInputElement>, identifier: string) => {
        // Copy the rows and find the row index
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);
        if (rowIndex === -1) return;

        // Set the whole centimeters in the row
        updatedRows[rowIndex] = { ...updatedRows[rowIndex], [field]: parseDimensionInput(event.target.value) };
        rowsRef.current = updatedRows;
        setRows(updatedRows);
        setUpdatedData((prev: any) => ({ ...prev, [name]: updatedRows }));

        // Only lines of a width x depth priced product take their price from the matrix
        const row = updatedRows[rowIndex];
        if (!row.product || row.product_pricing_method !== 'dimensions') return;
        const matrix = await resolveProductMatrix(row.product);
        applyMatrixPrice(identifier, matrix);
    };

    // Handle adding of a line image, read as data url for the upload and the preview
    const handleAddLineImage = (file: File | null, identifier: string) => {
        if (!file || !file.type.startsWith('image/')) return;

        const reader = new FileReader();
        reader.onload = (loadEvent: ProgressEvent<FileReader>) => {
            const result = loadEvent.target?.result as string;
            const extension = file.name.substring(file.name.lastIndexOf('.'));
            const randomCode = Math.random().toString(36).substring(2, 7);
            updateRows(identifier, (item) => ({
                ...item,
                image_file: result,
                image_filename: `line_image_${randomCode}${extension}`,
                image_deleted: false,
            }));
        };
        reader.readAsDataURL(file);
    };

    // Handle removing of a line image
    const handleRemoveLineImage = (identifier: string) => {
        updateRows(identifier, (item) => ({
            ...item,
            image: null,
            image_file: null,
            image_filename: null,
            image_deleted: true,
        }));
    };

    // Resolve the url of a saved line image, local environments load it from the backend
    const resolveLineImageUrl = (row: DocumentLineType): string | null => {
        if (row.image_file) return row.image_file;
        if (!row.image || row.image_deleted) return null;
        if (row.image.startsWith('http') || process.env.NODE_ENV === 'production') return row.image;
        return `${baseUrl}${row.image}`;
    };

    // Show the size fields for width x depth priced products and for lines which already have a size
    const showDimensionFields = (row: DocumentLineType): boolean => {
        if (!dimensionPricingActive) return false;
        return row.product_pricing_method === 'dimensions' || (row.width !== null && row.width !== undefined) || (row.depth !== null && row.depth !== undefined);
    };

    // Render the image of a line above its whole row, aligned with the description column
    const renderLineImageRow = (row: DocumentLineType, rowClassName: string) => {
        const imageUrl = resolveLineImageUrl(row);
        if (!imageUrl) return null;
        return (
            <div className={`${rowClassName} line-image-row`}>
                <div></div>
                <div className='line-image-preview'>
                    <div className='image' style={{ backgroundImage: `url(${imageUrl})` }}>
                        {editing && (
                            <span className='remove-button' onClick={() => handleRemoveLineImage(row.id?.toString() || row.uuid || '')}>
                                <FontAwesomeIcon icon={faTimes} />
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Render the description with the size of a line below it in view mode
    const renderViewDescription = (row: DocumentLineType) => {
        const hasSize = (row.width !== null && row.width !== undefined) || (row.depth !== null && row.depth !== undefined);
        return (
            <div className='description-cell'>
                <div dangerouslySetInnerHTML={{ __html: row.description as string }}></div>
                {hasSize && (
                    <div className='line-dimensions-view'>
                        {t('document_lines.dimensions.view_format', { width: row.width ?? '-', depth: row.depth ?? '-' })}
                    </div>
                )}
            </div>
        );
    };

    // Apply the vat reverse charge or default vat rate to a newly created row
    const applyDefaultVatRateToRow = (newRow: DocumentLineType) => {
        if (showVatReverseCharge) {
            // Find the first vat rate with the vat reverse charge property
            const vatReverseChargeRate = dropdownData?.vatrate?.results?.find(
                (vatRate: VatRateType) => vatRate.vat_reverse_charge === true
            );

            if (vatReverseChargeRate) {
                newRow.vat_rate = vatReverseChargeRate.id;
            }

        // Otherwise try to find if a default vat rate is set, set this as the vat rate
        } else {
            // Find the default vat rate
            const defaultVatRate = dropdownData?.vatrate?.results?.find(
                (vatRate: VatRateType) => vatRate.is_default === true
            );

            if (defaultVatRate) {
                newRow.vat_rate = defaultVatRate.id;
            }
        }
    };

    // Handle the creation of a new row, optionally scoped to a section
    const handleAddRow = (sectionId?: string) => {
        // Determine the currency and position for the new row
        const currency = data['currency'] ? data['currency'] : defaultCurrency
        const nonDeletedSections = sections.filter(section => !section.deleted);

        let newPosition: number;
        let groupValue: string | number | null;

        if (nonDeletedSections.length > 0 && sectionId) {
            // Position the new row at the end of its own section
            const isFirstSection = sectionIdentifierOf(nonDeletedSections[0]) === sectionId;
            newPosition = rowsOfSection(sectionId, isFirstSection).length + 1;
            groupValue = sectionId;
        } else {
            // Without sections, position the new row at the end of the whole document
            const nonDeletedRows = rows.filter(row => !row.deleted);
            newPosition = nonDeletedRows.length + 1;
            groupValue = null;
        }

        const newRow = createNewDocumentLine(documentId!, featureName as 'proposal' | 'order' | 'invoice', uuidv4(), currency, newPosition, groupValue);
        applyDefaultVatRateToRow(newRow);

        // Add the new row at the end of the list
        const updatedRows = [...rows, newRow];

        // Update the rows and updated data
        setRows(updatedRows);
        setUpdatedData((prev: any) => ({ ...prev, [name]: updatedRows }))
    };

    // Handle deletion of a row
    const handleDeleteRow = (identifier: string) => {
        const updatedRows = rows.map(row => {
            const rowIdentifier = row.id?.toString() || row.uuid;
            return rowIdentifier === identifier ? { ...row, deleted: true } : row;
        });

        // Renumber the remaining rows, scoped to their own section when sections are used
        const finalRows = renumberRows(updatedRows, sections);

        setRows(finalRows);
        setUpdatedData((prev: any) => ({ ...prev, [name]: finalRows }))
    };

    // Handle ordering change of the rows, only used when sections are not used
    const handleOrderingChange = (result: DropResult) => {
        if (!result.destination) return;
        const { source, destination } = result;

        // Only use the non deleted rows to determine the new ordering
        const nonDeletedRows = rows.filter(row => !row.deleted);
        const [reorderedRow] = nonDeletedRows.splice(source.index, 1);
        nonDeletedRows.splice(destination.index, 0, reorderedRow);

        // Determine the ordering for the non deleted rows
        const updatedNonDeletedRows = nonDeletedRows.map((row, index) => ({
            ...row,
            position: index + 1,
        }));

        // Add the deleted rows to the list without changing their ordering
        const finalRows = [...updatedNonDeletedRows, ...rows.filter(row => row.deleted)];

        setRows(finalRows);
        setUpdatedData((prev: any) => ({ ...prev, [name]: finalRows }))
    };

    // Handle ordering change of the rows within one section
    const handleSectionLineOrderingChange = (result: DropResult, sectionId: string, isFirstSection: boolean) => {
        if (!result.destination) return;
        const { source, destination } = result;

        // Splice the reordered row within this section's own rows
        const sectionRows = rowsOfSection(sectionId, isFirstSection);
        const [reorderedRow] = sectionRows.splice(source.index, 1);
        sectionRows.splice(destination.index, 0, reorderedRow);

        // Rebuild the full rows array, replacing this section's rows in their new order
        let sectionRowIndex = 0;
        const updatedRows = rows.map(row => {
            const rowGroup = rowSectionIdentifier(row);
            const belongsToSection = !row.deleted && (rowGroup === sectionId || (isFirstSection && rowGroup === undefined));
            return belongsToSection ? sectionRows[sectionRowIndex++] : row;
        });

        // Renumber every section's rows to keep the positions consistent
        const finalRows = renumberRows(updatedRows, sections);

        setRows(finalRows);
        setUpdatedData((prev: any) => ({ ...prev, [name]: finalRows }))
    };

    // Handle the creation of a new section
    const handleAddSection = () => {
        const currency = data['currency'] ? data['currency'] : defaultCurrency;
        const nonDeletedSections = sections.filter(section => !section.deleted);

        // The first time a section is added, materialize the first section from the current rows
        if (nonDeletedSections.length === 0) {
            const firstSectionUuid = uuidv4();
            const firstSection = createNewDocumentLineGroup(firstSectionUuid, 1);
            const secondSectionUuid = uuidv4();
            const secondSection = createNewDocumentLineGroup(secondSectionUuid, 2);

            // Attach every current non deleted row to the new first section
            const reassignedRows = rows.map(row => row.deleted ? row : { ...row, group: firstSectionUuid });

            // Seed the new section with one empty row
            const newRow = createNewDocumentLine(documentId!, featureName as 'proposal' | 'order' | 'invoice', uuidv4(), currency, 1, secondSectionUuid);
            applyDefaultVatRateToRow(newRow);

            const finalSections = [firstSection, secondSection];
            const finalRows = renumberRows([...reassignedRows, newRow], finalSections);

            setSections(finalSections);
            setRows(finalRows);
            setUpdatedData((prev: any) => ({ ...prev, line_groups: finalSections, [name]: finalRows }));

        // Otherwise simply append a new section with one empty row
        } else {
            const newSectionUuid = uuidv4();
            const newSection = createNewDocumentLineGroup(newSectionUuid, nonDeletedSections.length + 1);
            const newRow = createNewDocumentLine(documentId!, featureName as 'proposal' | 'order' | 'invoice', uuidv4(), currency, 1, newSectionUuid);
            applyDefaultVatRateToRow(newRow);

            const updatedSections = [...sections, newSection];
            const finalRows = renumberRows([...rows, newRow], updatedSections);

            setSections(updatedSections);
            setRows(finalRows);
            setUpdatedData((prev: any) => ({ ...prev, line_groups: updatedSections, [name]: finalRows }));
        }
    };

    // Handle deletion of a section, its rows are soft deleted along with it
    const handleDeleteSection = (sectionId: string) => {
        const updatedSections = sections.map(section => sectionIdentifierOf(section) === sectionId ? { ...section, deleted: true } : section);
        const isFirstSection = sections.filter(section => !section.deleted)[0] && sectionIdentifierOf(sections.filter(section => !section.deleted)[0]) === sectionId;
        const updatedRows = rows.map(row => {
            const rowGroup = rowSectionIdentifier(row);
            const belongsToSection = rowGroup === sectionId || (isFirstSection && rowGroup === undefined);
            return belongsToSection ? { ...row, deleted: true } : row;
        });

        const finalRows = renumberRows(updatedRows, updatedSections);

        setSections(updatedSections);
        setRows(finalRows);
        setUpdatedData((prev: any) => ({ ...prev, line_groups: updatedSections, [name]: finalRows }));
    };

    // Handle ordering change of the sections
    const handleSectionOrderingChange = (result: DropResult) => {
        if (!result.destination) return;
        const { source, destination } = result;

        const nonDeletedSections = sections.filter(section => !section.deleted);
        const [reorderedSection] = nonDeletedSections.splice(source.index, 1);
        nonDeletedSections.splice(destination.index, 0, reorderedSection);

        const updatedNonDeletedSections = nonDeletedSections.map((section, index) => ({
            ...section,
            position: index + 1,
        }));

        const finalSections = [...updatedNonDeletedSections, ...sections.filter(section => section.deleted)];

        setSections(finalSections);
        setUpdatedData((prev: any) => ({ ...prev, line_groups: finalSections }));
    };

    // Dispatches a drag end result to the section or section lines handler, based on its type
    // A single shared DragDropContext is used because react-beautiful-dnd does not support nested contexts
    const handleGroupedDragEnd = (result: DropResult) => {
        if (result.type === 'section') {
            handleSectionOrderingChange(result);
        } else if (result.type.startsWith('section-lines-')) {
            const sectionId = result.type.replace('section-lines-', '');
            const isFirstSection = nonDeletedSections[0] && sectionIdentifierOf(nonDeletedSections[0]) === sectionId;
            handleSectionLineOrderingChange(result, sectionId, isFirstSection);
        }
    };

    // Generic function to update a section with a changed value
    const updateSections = (sectionId: string, updateFunction: (item: DocumentLineGroupType) => DocumentLineGroupType) => {
        const updatedSections = sections.map(section => sectionIdentifierOf(section) === sectionId ? updateFunction(section) : section);

        setSections(updatedSections);
        setUpdatedData((prev: any) => ({ ...prev, line_groups: updatedSections }));
    };

    // Handle the section title change
    const handleSectionTitleChange = (event: React.ChangeEvent<HTMLInputElement>, sectionId: string) => {
        updateSections(sectionId, (section) => ({ ...section, title: event.target.value }));
    };

    // Handle the section description change
    const handleSectionDescriptionChange = (value: string | React.ChangeEvent<HTMLTextAreaElement>, sectionId: string) => {
        updateSections(sectionId, (section) => ({ ...section, description: String(value) }));
    };

    // Format the total price value
    const formatPrice = (row: DocumentLineType, fieldName: 'sale_price' | 'total_price', showFallback?: boolean): string => {

        // Get the value from the field
        const fieldValue = row[fieldName];

        // If a value exists, format the value
        if (fieldValue) {

            let currency;
            if (row.sale_price_currency) {
                // If a sale price currency is found, use the currency of the product
                currency = row.sale_price_currency.toUpperCase();
            } else {
                // Otherwise fallback to the default currency of the environment
                currency = defaultCurrency?.toUpperCase() || 'EUR';
            }

            // Format the total price
            const formattedTotalPrice = formatPriceCurrencyString(fieldValue.toString(), userLocale, currency);

            return formattedTotalPrice;

        // If no value exists, format 0 as fallback
        } else if (showFallback) {
            const formattedZero = formatPriceString("0", userLocale);

            return `${defaultCurrencySymbol} ${formattedZero}`
        } else {
            return '';
        }
    }

    // Calculate the total amount of the entered rows
    const totalAmountOfEnteredRows = (rows: DocumentLineType[]): number => {
        const totalAmount = rows
            .filter(row => !row.deleted)
            .reduce((acc, row) => {
                // Convert total price strings to a number
                const totalPrice = typeof row.total_price === 'string'
                    ? parseFloat(row.total_price)
                    : row.total_price ?? 0;

                return acc + totalPrice;
            }, 0);

        return totalAmount;
    };

    // Calculate the subtotal of the document lines
    const subTotalPrice = (rows: DocumentLineType[]): number => {

        // For vat excluded document lines, sum the amount of the entered rows
        if (vatCalculationMethod === 'vat_excluded') {
            const totalAmount = totalAmountOfEnteredRows(rows)

            return totalAmount;
        }

        // For vat included document lines, subtract the vat amount from the total amount of the entered rows
        else {
            // Calculate the total amount of the entered rows
            const totalAmountOfRows = totalAmountOfEnteredRows(rows);

            // Calculate the total amount of vat
            const vatTotal = vatSummary(rows, vatRates).reduce((acc, vat) => acc + vat.amount, 0);

            // Subtract the vat amount from the total amount of the entered rows
            const subTotal = totalAmountOfRows - vatTotal;

            return subTotal;
        }
    };

    // Calculate the subtotal of one section
    const sectionSubTotalPrice = (sectionId: string, isFirstSection: boolean): number => {
        return subTotalPrice(rowsOfSection(sectionId, isFirstSection));
    };

    // Calculate the total amount per used vat rate
    const vatSummary = (rows: DocumentLineType[], vatRates: VatRateType[]): { name: string; amount: number }[] => {
        const vatAmountPerVatRate = vatRates
            // Filter on the used vat rates in the document lines
            .filter(vatRate => rows.some(row => row.vat_rate === vatRate.id))
            .map(vatRate => {
                // Calculate the total amount per vat rate
                const subtotal = rows
                    .filter(row => !row.deleted && row.vat_rate === vatRate.id)
                    .reduce((acc, row) => {
                        // Convert total price strings to a number
                        const totalPrice = typeof row.total_price === 'string'
                            ? parseFloat(row.total_price)
                            : row.total_price ?? 0;

                        return acc + totalPrice;
                    }, 0);

                // For vat excluded document lines, multiply the amount by the vat percentage
                if (vatCalculationMethod === 'vat_excluded') {

                    // Convert the vat percentage to a decimal
                    const vatPercentageDecimal = ((vatRate.percentage ?? 0) / 100);

                    // Calculate the amount for this vat rate
                    const taxAmount = subtotal * vatPercentageDecimal;

                    return { name: vatRate.name, amount: taxAmount };
                }

                // For vat included document lines, divide the amount by the vat percentage
                else {

                    // Convert the vat percentage to a decimal
                    const vatPercentageDecimal = ((vatRate.percentage ?? 0) / 100);

                    // Convert the vat percentage to a gross multiplier
                    const vatPercentageGrossDecimal = (vatPercentageDecimal + 1)

                    // Calculate the tax excluded part of the amount of the vat rate
                    const taxExcludedAmount = subtotal / vatPercentageGrossDecimal

                    // Subtract the tax excluded amount from the subtotal to get the tax amount
                    const taxAmount = subtotal - taxExcludedAmount;

                    return { name: vatRate.name, amount: taxAmount };
                }
            });

        return vatAmountPerVatRate;
    };

    // Calculate the total price of the document lines
    const totalPrice = (rows: DocumentLineType[], vatRates: VatRateType[]): number => {

        // For vat excluded document lines, sum the amount of the entered rows plus the vat amount
        if (vatCalculationMethod === 'vat_excluded') {

            // Calculate the total amount of the entered rows
            const subTotal = totalAmountOfEnteredRows(rows);

            // Calculate the total amount of vat
            const vatTotal = vatSummary(rows, vatRates).reduce((acc, vat) => acc + vat.amount, 0);

            // Set the total price
            return subTotal + vatTotal;
        }

        // For vat included document lines, sum the amount of the entered rows
        else {
            const totalAmount = totalAmountOfEnteredRows(rows)

            return totalAmount;
        }
    };

    const formatTotalValue = (value: number) => {
        return formatPriceCurrencyString(value.toString(), userLocale, selectedCurrency);
    }

    // Non deleted sections in position order, used to decide between the flat and grouped rendering
    const nonDeletedSections = sections.filter(section => !section.deleted);
    const isGrouped = nonDeletedSections.length > 0;

    // Render one document line row, shared between the flat and grouped edit mode rendering
    const renderLineRow = (row: DocumentLineType, index: number, rowCount: number) => {
        const identifier = row.id?.toString() || row.uuid;
        if (identifier === undefined) return null;

        return (
            <Draggable key={identifier} draggableId={`item-${identifier}`} index={index} isDragDisabled={rowCount <= 1}>
                {(provided) => (
                    <div className='list-item draggable' ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                        {lineImagesAllowed && renderLineImageRow(row, 'document-lines-row')}
                        <div className='document-lines-row'>
                            <input
                                type="text"
                                id={`quantity_string_${identifier}`}
                                name={`quantity_string_${identifier}`}
                                value={row.quantity_string ?? ''}
                                onChange={event => handleQuantityChange(event, identifier || '')}
                                placeholder={t('invoice.lines.quantity_placeholder')}
                                autoFocus={false}
                                className={identifier && errorMessages?.lines?.[identifier]?.['quantity'] ? 'is-invalid' : undefined}
                            />
                            <div>
                                {visibleSearchField === identifier && (
                                    <div className='product-search-field'>
                                        <SearchField<ProductType>
                                            key={identifier}
                                            type='searchfield'
                                            name='name'
                                            onSelect={(selectedItem) => handleSelectProduct(selectedItem, identifier || '')}
                                            placeholder='invoice.lines.search_item_label'
                                            query={{
                                                endpoint: 'get_product_list',
                                                params: { is_active: true }
                                            }}
                                            optionFormat={{
                                                title: { field: 'name', format: (value) => value },
                                                bracket: { field: 'number', format: (value) => value, optional: true },
                                                subtitle: { field: 'description_plain', format: (value) => value, optional: true }
                                            }}
                                            autoFocus={true}
                                        />
                                    </div>
                                )}
                                <div className='textarea-search-button-wrapper'>
                                    <TextareaInput
                                        name={name}
                                        identifier={identifier || ''}
                                        value={row.description ?? undefined}
                                        placeholder='invoice.lines.description_placeholder'
                                        rows={2}
                                        enableStyling='simple'
                                        showToolbarInitially={false}
                                        onChange={event => handleTextAreaChange(event, identifier || '')}
                                        onTabClose={() => handleTextareaTab(identifier || '')}
                                    />
                                    <div className='product-search-icon tooltip-icon'>
                                        <FontAwesomeIcon
                                            icon={faSearch}
                                            onClick={() => setVisibleSearchField(prev => prev === identifier ? null : identifier)}
                                        />
                                        <span className="tooltip">
                                            {visibleSearchField === identifier ? (
                                                t('invoice.lines.close_search_field_tooltip')
                                            ) : (
                                                t('invoice.lines.search_product_tooltip')
                                            )}
                                        </span>
                                    </div>
                                    {lineImagesAllowed && (
                                        <div className='line-image-icon tooltip-icon'>
                                            <FontAwesomeIcon
                                                icon={faImage}
                                                onClick={() => document.getElementById(`line_image_${identifier}`)?.click()}
                                            />
                                            <span className="tooltip">{t('document_lines.dimensions.add_image_tooltip')}</span>
                                            <input
                                                type='file'
                                                id={`line_image_${identifier}`}
                                                accept='image/*'
                                                style={{ display: 'none' }}
                                                onChange={event => { handleAddLineImage(event.target.files?.[0] ?? null, identifier || ''); event.target.value = ''; }}
                                            />
                                        </div>
                                    )}
                                </div>
                                {showDimensionFields(row) && (
                                    <div className='line-dimensions-fields'>
                                        <input
                                            type='text'
                                            inputMode='numeric'
                                            id={`width_${identifier}`}
                                            name={`width_${identifier}`}
                                            value={row.width ?? ''}
                                            onChange={event => handleDimensionChange('width', event, identifier || '')}
                                            placeholder={t('document_lines.dimensions.width_label')}
                                            className='dimension-input'
                                        />
                                        <span className='dimension-separator'>x</span>
                                        <input
                                            type='text'
                                            inputMode='numeric'
                                            id={`depth_${identifier}`}
                                            name={`depth_${identifier}`}
                                            value={row.depth ?? ''}
                                            onChange={event => handleDimensionChange('depth', event, identifier || '')}
                                            placeholder={t('document_lines.dimensions.depth_label')}
                                            className='dimension-input'
                                        />
                                        <span className='dimension-unit'>{t('document_lines.dimensions.unit')}</span>
                                        {missingMatrixPrice[identifier || ''] && (
                                            <span className='dimension-warning'>{t('document_lines.dimensions.no_matrix_price')}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                            <input
                                type="text"
                                id={`sale_price_string_${identifier}`}
                                name={`sale_price_string_${identifier}`}
                                value={row.sale_price_string ?? undefined}
                                onChange={event => handlePriceChange(event, identifier || '')}
                                placeholder={t('invoice.lines.price_placeholder')}
                                className={`price-field ${identifier && errorMessages?.lines?.[identifier]?.['sale_price'] ? 'is-invalid' : ''}`}
                            />
                            <div className='string-value price-value'>
                                <div className='text-right'>{formatPrice(row, 'total_price', true)}</div>
                            </div>
                            <div className='multi-line'>
                                <Dropdown<VatRateType>
                                    options={vatRates}
                                    id={`vat_rate_${identifier}`}
                                    name={`vat_rate_${identifier}`}
                                    disabled_selected={t('invoice.lines.vat_rate_placeholder')}
                                    selectedOption={vatRates.find(vatRate => row.vat_rate === vatRate.id)}
                                    onChange={(selectedValue) => handleVatRateChange(selectedValue, row.id?.toString() || row.uuid || '')}
                                    selectionFormat={(option) => `${option.name}`}
                                    optionFormat='name'
                                    allowNoneOption={false}
                                />
                                <Dropdown<LedgerAccountType>
                                    options={ledgerAccounts}
                                    id={`ledger_account_${identifier}`}
                                    name={`ledger_account_${identifier}`}
                                    disabled_selected={t('invoice.lines.ledger_account_placeholder')}
                                    selectedOption={ledgerAccounts.find(ledgerAccount => row.ledger_account === ledgerAccount.id)}
                                    onChange={(selectedValue) => handleLedgerAccountChange(selectedValue, row.id?.toString() || row.uuid || '')}
                                    selectionFormat={(option) => `${option.name}`}
                                    optionFormat='ledgeraccount_name_code'
                                    allowNoneOption={true}
                                />
                            </div>
                            <div className='delete-icon tooltip-icon'>
                                <FontAwesomeIcon
                                    icon={faTrash}
                                    onClick={() => handleDeleteRow(row.id?.toString() || row.uuid || '')} />
                                <span className="tooltip">{t('general.delete')}</span>
                            </div>
                            <div className={`order-icon tooltip-icon ${rowCount === 1 ? 'visibility-hidden' : ''}`}>
                                <FontAwesomeIcon icon={faGripLines} />
                                <span className="tooltip">{t('general.reorder')}</span>
                            </div>
                        </div>
                    </div>
                )}
            </Draggable>
        );
    };

    // Render the rows header, shared between the flat and grouped edit mode rendering
    const renderRowsHeader = () => (
        <div className='rows-header document-lines-row'>
            <div className='header-item'>{t('invoice.lines.quantity_label')}</div>
            <div className='header-item'>{t('invoice.lines.description_label')}</div>
            <div className='header-item'>{t('invoice.lines.price_label')}</div>
            <div className='header-item'>{t('invoice.lines.total_label')}</div>
            <div className='header-item'>{t('invoice.lines.extra_label')}</div>
            <div className='delete-placeholder'></div>
            <div className='drag-placeholder'></div>
        </div>
    );

    return (
        <div className='document-lines'>
            {showErrorAlert &&
                <div className="alert form-alert alert-danger" role="alert">
                    {t(errorMessages.general, { defaultValue: errorMessages.general })}
                </div>
            }
            {editing ? (
                // Edit mode
                <div className='edit-mode'>
                    <div className='document-lines-settings'>
                        <Dropdown<{ name: string, value: string }>
                            options={priceDisplayModes}
                            id='display_mode'
                            name='display_mode'
                            disabled_selected={t('forms.general_disabled_selected')}
                            selectedOption={priceDisplayMode}
                            onChange={(selectedValue) => handlePriceDisplayModeChange(selectedValue)}
                            selectionFormat={(option) => `${t(option.name)}`}
                            optionFormat='name'
                            allowNoneOption={false}
                            showSearch={false}
                        />
                        <TabsButton
                            buttons={[{
                                label: 'document_lines.general.vat_excluded_label',
                                tabValue: 'vat_excluded',
                            },
                            {
                                label: 'document_lines.general.vat_included_label',
                                tabValue: 'vat_included',
                            }]}
                            currentTabValue={vatCalculationMethod}
                            onTabsClick={(value) => handleVatCalculationMethodChange(value as 'vat_included' | 'vat_excluded')}
                            size="extra-small"
                        />
                    </div>

                    {isGrouped ? (
                        // Grouped rendering: sections, each with their own draggable lines
                        // A single shared DragDropContext dispatches by result.type, react-beautiful-dnd does not support nesting contexts
                        <DragDropContext onDragEnd={handleGroupedDragEnd}>
                            <StrictModeDroppable droppableId="sections-droppable" type="section">
                                {(sectionsProvided) => (
                                    <div {...sectionsProvided.droppableProps} ref={sectionsProvided.innerRef}>
                                        {nonDeletedSections.map((section, sectionIndex) => {
                                            const sectionId = sectionIdentifierOf(section);
                                            const isFirstSection = sectionIndex === 0;
                                            const isLastSection = sectionIndex === nonDeletedSections.length - 1;
                                            const sectionRows = rowsOfSection(sectionId, isFirstSection);
                                            const showDescriptionField = visibleDescriptionField === sectionId || !!section.description;

                                            return (
                                                <Draggable key={sectionId} draggableId={`section-${sectionId}`} index={sectionIndex} isDragDisabled={nonDeletedSections.length <= 1}>
                                                    {(sectionProvided) => (
                                                        <div className='document-line-section' ref={sectionProvided.innerRef} {...sectionProvided.draggableProps}>
                                                            <div className='document-line-section-header'>
                                                                <input
                                                                    type="text"
                                                                    value={section.title}
                                                                    placeholder={t('document_lines.sections.title_placeholder')}
                                                                    onChange={(event) => handleSectionTitleChange(event, sectionId)}
                                                                    className='section-title-input'
                                                                />
                                                                <div className='section-header-actions'>
                                                                    {!showDescriptionField && (
                                                                        <div className='description-icon tooltip-icon'>
                                                                            <FontAwesomeIcon
                                                                                icon={faStickyNote}
                                                                                onClick={() => setVisibleDescriptionField(sectionId)}
                                                                            />
                                                                            <span className="tooltip">
                                                                                {t('document_lines.sections.add_description_tooltip')}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    <div className={`delete-icon tooltip-icon ${isFirstSection ? 'visibility-hidden' : ''}`}>
                                                                        <FontAwesomeIcon
                                                                            icon={faTrash}
                                                                            onClick={() => !isFirstSection && handleDeleteSection(sectionId)}
                                                                        />
                                                                        <span className="tooltip">{t('document_lines.sections.delete_tooltip')}</span>
                                                                    </div>
                                                                    <div {...sectionProvided.dragHandleProps} className={`order-icon tooltip-icon ${nonDeletedSections.length === 1 ? 'visibility-hidden' : ''}`}>
                                                                        <FontAwesomeIcon icon={faGripLines} />
                                                                        <span className="tooltip">{t('general.reorder')}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {showDescriptionField && (
                                                                <div className='section-description-field'>
                                                                    <TextareaInput
                                                                        name={`section_description_${sectionId}`}
                                                                        identifier={sectionId}
                                                                        value={section.description ?? ''}
                                                                        placeholder='document_lines.sections.description_placeholder'
                                                                        rows={1}
                                                                        enableStyling='simple'
                                                                        showToolbarInitially={false}
                                                                        onChange={event => handleSectionDescriptionChange(event, sectionId)}
                                                                    />
                                                                </div>
                                                            )}

                                                            {sectionRows.length > 0 && renderRowsHeader()}

                                                            <StrictModeDroppable droppableId={`section-lines-${sectionId}`} type={`section-lines-${sectionId}`}>
                                                                {(linesProvided) => (
                                                                    <div {...linesProvided.droppableProps} ref={linesProvided.innerRef}>
                                                                        {sectionRows.map((row, index) => renderLineRow(row, index, sectionRows.length))}
                                                                        {linesProvided.placeholder}
                                                                    </div>
                                                                )}
                                                            </StrictModeDroppable>

                                                            <div onClick={(e) => { e.preventDefault(); handleAddRow(sectionId); }} className="add-new-button">
                                                                {t('invoice.lines.add_new_item')}
                                                            </div>

                                                            {!isLastSection && sectionRows.length > 0 && (
                                                                <div className='sub-total-summary'>
                                                                    <div></div>
                                                                    <div className='text-right'>{t('invoice.lines.subtotal_label')}</div>
                                                                    <div className='text-right'>{formatTotalValue(sectionSubTotalPrice(sectionId, isFirstSection))}</div>
                                                                    <div></div>
                                                                    <div className='icons-placeholder'></div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {sectionsProvided.placeholder}
                                    </div>
                                )}
                            </StrictModeDroppable>
                        </DragDropContext>
                    ) : (
                        // Flat rendering, used until a document ever gets a second section
                        <>
                            {rows.length > 0 && renderRowsHeader()}
                            <DragDropContext onDragEnd={handleOrderingChange}>
                                <StrictModeDroppable droppableId="droppable">
                                    {(provided) => (
                                        <div {...provided.droppableProps} ref={provided.innerRef}>
                                            {rows.filter(row => !row.deleted).map((row, index) => renderLineRow(row, index, rows.filter(row => !row.deleted).length))}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </StrictModeDroppable>
                            </DragDropContext>
                            <div onClick={(e) => {e.preventDefault(); handleAddRow(); }}
                                className="add-new-button">
                                {t('invoice.lines.add_new_item')}
                            </div>
                        </>
                    )}
                    {errorMessages &&
                        <div className='error-message'>
                            {renderRowsFieldErrorMessages(errorMessages, 'lines', t)}
                        </div>
                    }
                    {helperText &&
                        <div className="helper-text">
                            {t(helperText)}
                        </div>
                    }
                    {rows.filter(row => !row.deleted).length > 0 && (
                        <div className='document-lines-totals'>
                            {vatSummary(rows, vatRates).length > 0 && (
                                // Only show sub total if there are vat rates
                                <div className='sub-total-summary'>
                                    <div></div>
                                    <div className='text-right'>{t('invoice.lines.subtotal_label')}</div>
                                    <div className='text-right'>
                                        {formatTotalValue(
                                            isGrouped
                                                ? sectionSubTotalPrice(sectionIdentifierOf(nonDeletedSections[nonDeletedSections.length - 1]), nonDeletedSections.length === 1)
                                                : subTotalPrice(rows)
                                        )}
                                    </div>
                                    <div></div>
                                    <div className='icons-placeholder'></div>
                                </div>
                            )}

                            {vatSummary(rows, vatRates).map((vatRate, index) => (
                                <div key={index} className='vat-summary'>
                                    <div></div>
                                    <div className='text-right'>{vatRate.name}</div>
                                    <div className='text-right'>{formatPriceCurrencyString(vatRate.amount.toString(), userLocale, selectedCurrency)}</div>
                                    <div></div>
                                    <div className='icons-placeholder'></div>
                                </div>
                            ))}

                            <div className='total-summary'>
                                <div></div>
                                <div className='text-right'>{t('invoice.lines.total_label')}</div>
                                <div className='text-right'>{formatTotalValue(totalPrice(rows, vatRates))}</div>
                                <div></div>
                                <div className='icons-placeholder'></div>
                            </div>
                        </div>
                    )}
                    {showVatReverseCharge && featureName === 'invoice' && (
                        <div className="vat-reverse-charge">
                            <div className='vat-reverse-charge-label'>
                                {t('invoice.lines.vat_reverse_charge_label')}
                            </div>
                            <input
                                type="text"
                                id='vat_reverse_charge_number'
                                name='vat_reverse_charge_number'
                                value={vatReverseChargeNumber ?? ''}
                                onChange={(event) => handleVatReverseChargeNumberChange(event)}
                                placeholder={t('invoice.lines.quantity_placeholder')}
                                autoFocus={false}
                                className='vat-reverse-charge-field'
                            />
                            <div></div>
                        </div>
                    )}
                </div>
            ) : (
                // View mode
                <div className={`full-width-alignment ${isEditable?.editability && !userHasOnlyViewRights ? 'editable' : ''}`}>
                    <div className="view-mode">
                        <span className='p'>
                            <div className='document-lines-table'>
                                {(() => {
                                    // Only saved sections are shown in view mode, matching how unsaved rows are hidden too
                                    const savedSections = sections.filter(section => !section.deleted && section.id !== null);

                                    if (savedSections.length > 0) {
                                        return savedSections.map((section, sectionIndex) => {
                                            const sectionId = sectionIdentifierOf(section);
                                            const isFirstSection = sectionIndex === 0;
                                            const isLastSection = sectionIndex === savedSections.length - 1;
                                            const sectionRows = rowsOfSection(sectionId, isFirstSection).filter(row => row.id);

                                            return (
                                                <div key={sectionId} className='document-line-section-view'>
                                                    {savedSections.length > 1 && (
                                                        <div className='section-title-view bold'>{section.title}</div>
                                                    )}
                                                    {section.description && (
                                                        <div className='section-description-view' dangerouslySetInnerHTML={{ __html: section.description }}></div>
                                                    )}
                                                    {sectionRows.length > 0 && (
                                                        <div className='table-header'>
                                                            <div className='header-item'>{t('invoice.lines.quantity_label')}</div>
                                                            <div className='header-item'>{t('invoice.lines.description_label')}</div>
                                                            {priceDisplayMode.value === 'line_prices' ? (
                                                                <div className='header-item text-right'>{t('invoice.lines.price_label')}</div>
                                                            ) : (
                                                                <div></div>
                                                            )}
                                                            <div className='header-item text-right'>{t('invoice.lines.total_label')}</div>
                                                            <div className='header-item text-right'>{t('invoice.lines.vat_rate_label')}</div>
                                                        </div>
                                                    )}
                                                    {sectionRows.map((row, index, filteredRows) => {
                                                        const vatRate = vatRates.find(vatRate => vatRate.id === row.vat_rate);
                                                        const tableRowClassName = `table-row ${index === filteredRows.length - 1 ? 'last-border' : ''}`;

                                                        return (
                                                            <React.Fragment key={row.id!.toString()}>
                                                            {renderLineImageRow(row, 'table-row')}
                                                            <div className={tableRowClassName}>
                                                                <div>{row.quantity_string} </div>
                                                                {renderViewDescription(row)}
                                                                {priceDisplayMode.value === 'line_prices' ? (
                                                                    <>
                                                                        <div className='text-right'>{formatPrice(row, 'sale_price', true)}</div>
                                                                        <div className='text-right'>{formatPrice(row, 'total_price', true)}</div>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <div></div>
                                                                        <div></div>
                                                                    </>
                                                                )}
                                                                {vatRate &&
                                                                    <div className='text-right'>{vatRate.name}</div>
                                                                }
                                                            </div>
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                    {!isLastSection && sectionRows.length > 0 && (
                                                        <div className='sub-total-summary'>
                                                            <div></div>
                                                            <div className='text-right'>{t('invoice.lines.subtotal_label')}</div>
                                                            <div className='text-right'>{formatTotalValue(sectionSubTotalPrice(sectionId, isFirstSection))}</div>
                                                            <div></div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        });
                                    }

                                    // Flat view mode, used until a document ever gets a second section
                                    return (
                                        <>
                                            {rows.length > 0 && (
                                                <div className='table-header'>
                                                    <div className='header-item'>{t('invoice.lines.quantity_label')}</div>
                                                    <div className='header-item'>{t('invoice.lines.description_label')}</div>
                                                    {priceDisplayMode.value === 'line_prices' ? (
                                                        <div className='header-item text-right'>{t('invoice.lines.price_label')}</div>
                                                    ) : (
                                                        <div></div>
                                                    )}
                                                    <div className='header-item text-right'>{t('invoice.lines.total_label')}</div>
                                                    <div className='header-item text-right'>{t('invoice.lines.vat_rate_label')}</div>
                                                </div>
                                            )}
                                            {rows.filter(row => row.id).map((row, index, filteredRows) => {
                                                const vatRate = vatRates.find(vatRate => vatRate.id === row.vat_rate);
                                                const tableRowClassName = `table-row ${index === filteredRows.length - 1 ? 'last-border' : ''}`;

                                                return (
                                                    <React.Fragment key={row.id!.toString()}>
                                                    {renderLineImageRow(row, 'table-row')}
                                                    <div className={tableRowClassName}>
                                                        <div>{row.quantity_string} </div>
                                                        {renderViewDescription(row)}
                                                        {priceDisplayMode.value === 'line_prices' ? (
                                                            <>
                                                                <div className='text-right'>{formatPrice(row, 'sale_price', true)}</div>
                                                                <div className='text-right'>{formatPrice(row, 'total_price', true)}</div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div></div>
                                                                <div></div>
                                                            </>
                                                        )}
                                                        {vatRate &&
                                                            <div className='text-right'>{vatRate.name}</div>
                                                        }
                                                    </div>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </>
                                    );
                                })()}
                                {isEditable?.editability && !userHasOnlyViewRights && (
                                    // Only show add item label when the object is editable
                                    <div className='add-item'>
                                        {t('invoice.lines.add_item')}
                                    </div>
                                )}

                                {rows.filter(row => !row.deleted).length > 0 && (
                                    <>
                                        {vatSummary(rows, vatRates).length > 0 && (
                                            // Only show sub total if there are vat rates
                                            <div className='sub-total-summary'>
                                                <div></div>
                                                <div className='text-right'>{t('invoice.lines.subtotal_label')}</div>
                                                <div className='text-right'>
                                                    {formatTotalValue(
                                                        isGrouped
                                                            ? sectionSubTotalPrice(sectionIdentifierOf(nonDeletedSections[nonDeletedSections.length - 1]), nonDeletedSections.length === 1)
                                                            : subTotalPrice(rows)
                                                    )}
                                                </div>
                                                <div></div>
                                            </div>
                                        )}

                                        {vatSummary(rows, vatRates).map((vatRate, index) => (
                                            <div key={index} className='vat-summary'>
                                                <div></div>
                                                <div className='text-right'>{vatRate.name}</div>
                                                <div className='text-right'>{formatPriceCurrencyString(vatRate.amount.toString(), userLocale, selectedCurrency)}</div>
                                                <div></div>
                                            </div>
                                        ))}

                                        <div className='total-summary'>
                                            <div></div>
                                            <div className='text-right'>{t('invoice.lines.total_label')}</div>
                                            <div className='text-right'>{formatTotalValue(totalPrice(rows, vatRates))}</div>
                                            <div></div>
                                        </div>
                                    </>
                                )}
                            </div>
                            {showVatReverseCharge && featureName === 'invoice' && (
                                <div className="vat-reverse-charge">
                                    {data['recipient']?.['vat_number'] ? (
                                        t('invoice.lines.vat_reverse_charge_notice', { vat_number: data['recipient']?.['vat_number'] })
                                    ) : (
                                        t('invoice.lines.vat_reverse_charge_notice_unknown_vat')
                                    )}
                                </div>
                            )}
                        </span>
                        <span className='edit-icon'>
                            <FontAwesomeIcon icon={faPen} />
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DocumentLinesRowsField;
